import { isAxiosError } from 'axios';
import { Formik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useAlert,
  Button, CancelButton, CodeInput, ModalActions, ModalContent, ModalTitle, PhoneMask, SubmitButton,
  useVerifyContact
} from '@uplift/ui-v2';
import * as Yup from 'yup';

import { Box, Divider, FormHelperText, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { useQueryClient } from '@tanstack/react-query';
import { ProviderActivationEventName } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { useAddProviderActivationEventMutation } from '@uplift/queries';

import { useModalStyles } from './styles';
import { ContactName } from '@uplift/types';

const PHONE_REGEX = /\(\d{3}\) ?\d{3}-(?<lastfour>\d{4})/;

export type VerificationFormProps = {
  contactId: string;
  open: boolean;
  phone: string;
  loading?: boolean;
  onClose?: () => void;
  onEditMobileNumber: () => void;
  onSendNewCode?: () => void;
};

const ButtonTypography = withStyles(Button, {
  root: {
    textTransform: 'none',
    padding: 0,
    borderLeft: 0,
    lineHeight: 1,
    fontSize: '14px',

    '&:hover': {
      backgroundColor: 'inherit',
    }
  }
});


const validationSchema = Yup.object().shape({
  code: Yup.string().length(4).required(),
});

export const VerificationForm: React.FC<VerificationFormProps> = (props) => {
  const alert = useAlert();
  const { classes } = useModalStyles();
  const isMobile = useIsMobile();
  const isDesktop = !isMobile;
  const l10n = useL10n();
  const me = useCurrentuser();
  const queryClient = useQueryClient();
  const { verifyContact, error: verifyContactError } = useVerifyContact();
  const providerActivationEvent = useAddProviderActivationEventMutation();

  const helperErrorMessage = verifyContactError?.error
    ? l10n.phoneValidationModal.errors[verifyContactError.error]
    : undefined;

  const phoneDisplay = useMemo(() => {
    if (!props.phone) {
      return null;
    }

    const phoneMatch = props.phone.match(PHONE_REGEX);

    if (!phoneMatch?.groups) {
      return props.phone;
    }

    return (
      <span>
        <PhoneMask phone={props.phone} />
      </span>
    );
  }, [ props.phone ]);

  const handleSubmit = useCallback(async (values) => {
    try {
      await verifyContact({
        contactId: props.contactId,
        verificationCode: values.code,
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ 'user-contacts', ContactName.mobile ] }),
        providerActivationEvent.mutateAsync({
          providerId: me.id,
          eventName: ProviderActivationEventName.mfaAdded
        })
      ]);

      props.onClose?.();
    } catch (err) {
      if (isAxiosError(err) && err.response) {
        if (err.response.status === 400 && 'error' in err.response.data) {
          alert.error(err.response.data.error);
        }
      }
    }
  }, [ alert, me.id, props, providerActivationEvent, queryClient, verifyContact ]);

  const modalActions = useCallback((submitting: boolean) => {
    if (isDesktop) {
      return (
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button
            onClick={props.onSendNewCode}
            loading={props.loading}
            disabled={submitting}
            color="secondary"
          >
            {l10n.phoneValidationModal.sendNewCode}
          </Button>
          <Box>
            <CancelButton onClick={props.onClose}>
              Cancel
            </CancelButton>
            <SubmitButton
              size="medium"
              fullWidth={isMobile}
              variant="contained"
              color="secondary"
            >
              {l10n.phoneValidationModal.verifyCode}
            </SubmitButton>
          </Box>
        </Box>
      );
    }

    return (
      <SubmitButton
        size="large"
        fullWidth
        variant="contained"
        color="secondary"
      >
        {l10n.phoneValidationModal.verifyCode}
      </SubmitButton>
    );
  }, [ isDesktop, isMobile, l10n.phoneValidationModal.sendNewCode, l10n.phoneValidationModal.verifyCode, props.loading, props.onClose, props.onSendNewCode ]);

  return (
    <Formik
      validateOnMount
      validationSchema={validationSchema}
      enableReinitialize
      initialErrors={{
        code: '',
      }}
      initialValues={{
        contactId: props.contactId,
        phone: props.phone,
        code: '',
      }}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <>
          {isMobile && (
            <ModalTitle
              className={classes.mobileTitle}
              onClose={props.onClose}
            >
              Verify mobile number
            </ModalTitle>
          )}
          <ModalContent noPadding>
            <Box className={classes.modalContent}>
              <Box mt={{ xs: 2, sm: 3 }}>
                <Typography variant="h4">
                  {l10n.phoneValidationModal.enterCodeTextedTo} <strong>{phoneDisplay}</strong>
                </Typography>
              </Box>
              <Box mt={1}>
                <ButtonTypography size="small" color="secondary" onClick={props.onEditMobileNumber}>
                  {l10n.phoneValidationModal.editMobileNumber}
                </ButtonTypography>
              </Box>
              <Box mt={3}>
                <CodeInput
                  name="code"
                  codeLength={4}
                  onSubmit={formik.handleSubmit}
                  autoFocus
                />
              </Box>
              <Box mt={2}>
                <FormHelperText error={!!helperErrorMessage}>
                  {helperErrorMessage || l10n.phoneValidationModal.yourCodeWillExpire}
                </FormHelperText>
              </Box>
              {isMobile && (
                <Box mt={4}>
                  <ButtonTypography size="small" color="secondary" loading={props.loading} onClick={props.onSendNewCode}>
                    {l10n.phoneValidationModal.sendNewCode}
                  </ButtonTypography>
                </Box>
              )}
              {isDesktop && (
                <>
                  <Box mt={8}>
                    <Divider />
                  </Box>
                  <ModalActions>
                    {modalActions(formik.isSubmitting)}
                  </ModalActions>
                </>
              )}
            </Box>
          </ModalContent>
          {isMobile && (
            <>
              <Divider />
              <ModalActions>
                {modalActions(formik.isSubmitting)}
              </ModalActions>
            </>
          )}
        </>
      )}
    </Formik>
  );
};
